/**
 * Wrapper d'authentification.
 * Mode démo (VITE_DEMO_MODE=true) → mocks, pas besoin de Clerk.
 * Mode prod → Clerk via @clerk/clerk-react.
 *
 * Toutes les pages importent depuis ici, jamais directement depuis @clerk/clerk-react.
 */
import {
  useUser as useClerkUser,
  useAuth as useClerkAuth,
  SignedIn as ClerkSignedIn,
  SignedOut as ClerkSignedOut,
  UserButton as ClerkUserButton,
  SignIn as ClerkSignIn,
} from '@clerk/clerk-react';
import type { ReactNode } from 'react';

const MARIE_CLIENT_ID = '44444444-4444-4444-4444-444444444444';

export const IS_DEMO_MODE = import.meta.env.VITE_DEMO_MODE === 'true';

const DEMO_USER = {
  id: 'demo_user_sebastian',
  firstName: 'Sebastian',
  lastName: 'Dominguez',
  primaryEmailAddress: { emailAddress: 'sebastian@ducens.io' },
  publicMetadata: {
    role: 'admin' as string | undefined,
    allowed_clients: [MARIE_CLIENT_ID] as string[],
    default_client_slug: 'marie-dupont' as string,
  },
};

export type AuthUser = typeof DEMO_USER;

export function useUser(): {
  isLoaded: boolean;
  isSignedIn: boolean;
  user: AuthUser | null;
} {
  if (IS_DEMO_MODE) {
    return { isLoaded: true, isSignedIn: true, user: DEMO_USER };
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const c = useClerkUser();
  return {
    isLoaded: c.isLoaded,
    isSignedIn: c.isSignedIn ?? false,
    user: c.user
      ? {
          id: c.user.id,
          firstName: c.user.firstName ?? '',
          lastName: c.user.lastName ?? '',
          primaryEmailAddress: { emailAddress: c.user.primaryEmailAddress?.emailAddress ?? '' },
          publicMetadata: c.user.publicMetadata as AuthUser['publicMetadata'],
        }
      : null,
  };
}

export function useAuth(): {
  getToken: () => Promise<string | null>;
  signOut: () => Promise<void>;
} {
  if (IS_DEMO_MODE) {
    return {
      getToken: async () => 'demo-token',
      signOut: async () => {
        window.location.href = '/';
      },
    };
  }
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const c = useClerkAuth();
  return {
    getToken: c.getToken,
    signOut: c.signOut,
  };
}

export function SignedIn({ children }: { children: ReactNode }) {
  if (IS_DEMO_MODE) return <>{children}</>;
  return <ClerkSignedIn>{children}</ClerkSignedIn>;
}

export function SignedOut({ children }: { children: ReactNode }) {
  if (IS_DEMO_MODE) return null;
  return <ClerkSignedOut>{children}</ClerkSignedOut>;
}

export function UserButton({ className }: { className?: string }) {
  if (IS_DEMO_MODE) {
    return (
      <div
        className={className}
        title={DEMO_USER.primaryEmailAddress.emailAddress}
      >
        <div className="w-8 h-8 rounded-full bg-ducens-ink text-white flex items-center justify-center text-xs font-semibold">
          {DEMO_USER.firstName[0]}
          {DEMO_USER.lastName[0]}
        </div>
      </div>
    );
  }
  return <ClerkUserButton appearance={{ elements: { avatarBox: 'w-8 h-8' } }} />;
}

export function SignInWidget() {
  if (IS_DEMO_MODE) {
    return (
      <div className="bg-white border border-ducens-border rounded-lg p-6 text-center">
        <div className="text-sm text-ducens-muted mb-3">Mode démo activé</div>
        <a
          href="/clients/marie-dupont"
          className="inline-block px-4 py-2 rounded-md bg-ducens-ink text-white text-sm hover:opacity-90"
        >
          Entrer dans le dashboard
        </a>
      </div>
    );
  }
  return (
    <ClerkSignIn
      appearance={{
        elements: {
          rootBox: 'mx-auto',
          card: 'shadow-none border border-ducens-border',
        },
      }}
      routing="path"
      path="/sign-in"
    />
  );
}
